import { createStore } from 'zustand/vanilla'
import type { FerryNetwork } from '@traintopia/contracts'
import { api } from '../services/api'
import { registerFerryRoutes } from '../ui/colors'

// ─────────────────────────────────────────────────────────────────────────────
// NYC Ferry's static network: routes, landings and route lines.
//
// One lazy load, the first time anything asks — the map when the Ferry filter is picked, a
// ferry sheet opening from a favourite. ~60 KB, so it is loaded once per session and never
// on a subway-only one. Loading it also REGISTERS the route colours with `ui/colors`, which
// is what lets every existing route helper (badges, bullets, the vehicle sheet) paint a
// ferry id correctly; nothing ferry-coloured should draw before `network` is set.
//
// The live half — the boats — is not here: they arrive on the vehicle socket as ordinary
// `VehiclePosition`s with `type: 'ferry'`, into the transit store like every vehicle.
// ─────────────────────────────────────────────────────────────────────────────

interface FerryState {
  network: FerryNetwork | null
  loading: boolean
  /** Load the network if it isn't loaded or loading. Safe to call on every filter change. */
  ensureLoaded: () => void
}

const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000]

export const useFerryStore = createStore<FerryState>((set, get) => {
  let attempt = 0
  const load = () => {
    set({ loading: true })
    api
      .getFerryNetwork()
      .then((network) => {
        // The backend answers 503 with an EMPTY network when the operator's zip is down;
        // treat that as a failure to retry, not a loaded network with no boats.
        if (!network.routes.length) throw new Error('empty ferry network')
        registerFerryRoutes(network.routes)
        attempt = 0
        set({ network, loading: false })
      })
      .catch(() => {
        // A few spaced retries, then stand down: the next `ensureLoaded` (the rider picking
        // Ferry again) starts a fresh round rather than this polling for the whole session.
        if (attempt >= RETRY_DELAYS_MS.length) {
          attempt = 0
          set({ loading: false })
          return
        }
        setTimeout(load, RETRY_DELAYS_MS[attempt++])
      })
  }
  return {
    network: null,
    loading: false,
    ensureLoaded: () => {
      const s = get()
      if (s.network || s.loading) return
      load()
    },
  }
})
